import type {
  FormCheckboxStoryblok,
  FormConditionStoryblok,
  FormDateStoryblok,
  FormFieldsetStoryblok,
  FormFileUploadStoryblok,
  FormIbanStoryblok,
  FormInputStoryblok,
  FormMarketingOptInStoryblok,
  FormPageStoryblok,
  FormPostcodeStoryblok,
  FormRadioGroupStoryblok,
  FormSectionStoryblok,
  FormSelectStoryblok,
  FormStoryblok,
  FormStreetStoryblok,
  FormTextStoryblok,
  FormTextareaStoryblok,
  FormValidationConditionStoryblok,
  NewLineStoryblok,
} from '@/types/component-types-sb';

import { default as FormCheckbox } from './Fields/Checkbox';
import { default as FormDate } from './Fields/DateField';
import { default as FormFileUpload } from './Fields/FileUploadField';
import { default as FormFieldset } from './Fields/FormFieldset';
import FormText from './Fields/FormText';
import { default as FormIban } from './Fields/Iban';
import { default as FormInput } from './Fields/InputField';
import { default as FormMarketingOptIn } from './Fields/MarketingOptIn';
import { default as FormPostcode } from './Fields/Postcode';
import { default as FormRadioGroup } from './Fields/RadioButtonGroup';
import { default as FormSelect } from './Fields/SelectField';
import { default as FormStreet } from './Fields/Street';
import { default as FormTextarea } from './Fields/Textarea';

export type FormCondition = {
  [key: string]: ConditionMapType;
};

export type FieldValues = {
  [key: string]: string;
};

export type ConditionMapType = {
  show: boolean;
  required?: boolean;
};

export type FormFieldOption = {
  value: string;
  label?: string;
};

type FormFieldComponents =
  | FormCheckboxStoryblok
  | FormDateStoryblok
  | FormFieldsetStoryblok
  | FormFileUploadStoryblok
  | FormInputStoryblok
  | FormMarketingOptInStoryblok
  | FormPostcodeStoryblok
  | FormRadioGroupStoryblok
  | FormSelectStoryblok
  | FormStreetStoryblok
  | FormTextareaStoryblok
  | FormTextStoryblok;

export type FormFieldStoryblok =
  // | AutocompleteStoryblok
  | FormPageStoryblok
  | FormSectionStoryblok
  | FormTextareaStoryblok
  | FormIbanStoryblok
  | FormInputStoryblok
  | FormSelectStoryblok
  | FormDateStoryblok
  | FormRadioGroupStoryblok
  | FormTextStoryblok
  | FormFileUploadStoryblok
  | FormCheckboxStoryblok
  | FormRadioGroupStoryblok
  | FormFieldsetStoryblok
  | FormMarketingOptInStoryblok;

export type ComponentType =
  | 'FormIban'
  | 'FormInput'
  | 'FormPostcode'
  | 'FormStreet'
  | 'FormSelect'
  | 'FormFileUpload'
  | 'FormCheckbox'
  | 'FormTextarea'
  | 'FormDate'
  | 'FormText'
  | 'FormRadioGroup'
  | 'FormFieldset'
  | 'FormMarketingOptIn';

export type Validator = {
  component: string;
  errorMessage: string;
  /**
   * Preferred field name aligning with MaxLength.
   */
  minLength?: string;
  maxLength?: string;
  timeSpan?: string;
  timeUnit?: string;
  years?: string;
  allowedTypes?: ('' | 'pdf' | 'jpg' | 'jpeg' | 'png' | 'doc' | 'xml' | 'heic' | 'csv' | 'xls' | 'xlsx')[];
  maxFileSize?: string;
  maxFileAmount?: number;
  conditions?: FormValidationConditionStoryblok[];
};

export const getFormComponentByType = (componentType: ComponentType): unknown => {
  switch (componentType) {
    case 'FormInput':
      return FormInput;
    case 'FormPostcode':
      return FormPostcode;
    case 'FormStreet':
      return FormStreet;
    case 'FormIban':
      return FormIban;
    case 'FormSelect':
      return FormSelect;
    case 'FormFileUpload':
      return FormFileUpload;
    case 'FormCheckbox':
      return FormCheckbox;
    case 'FormTextarea':
      return FormTextarea;
    case 'FormRadioGroup':
      return FormRadioGroup;
    case 'FormDate':
      return FormDate;
    case 'FormText':
      return FormText;
    case 'FormFieldset':
      return FormFieldset;
    case 'FormMarketingOptIn':
      return FormMarketingOptIn;
  }
};

export type FormContentItem =
  // | Omit<AutocompleteStoryblok, 'text'>
  Omit<FormFieldComponents, 'text'> | FormTextStoryblok | FormFieldsetStoryblok | FormMarketingOptInStoryblok;

export type ContentBlockType = FormContentItem;

export type Validators =
  | 'Bic'
  | 'Date'
  | 'Email'
  | 'FileAmount'
  | 'FileTypes'
  | 'Iban'
  | 'MaxDate'
  | 'MaxFileSize'
  | 'MaxLength'
  | 'MinDate'
  | 'MinDateOfBirth'
  | 'MinLength'
  | 'Numeric'
  | 'Phone'
  | 'PostCode'
  | 'Regex'
  | 'Required';

export type FormContentWrapper = FormStoryblok & {
  content: (
    // | AutocompleteStoryblok
    | FormInputStoryblok
    | FormIbanStoryblok
    | FormCheckboxStoryblok
    | FormSectionStoryblok
    | FormPageStoryblok
    | FormSelectStoryblok
    | FormFileUploadStoryblok
    | FormTextareaStoryblok
    | FormDateStoryblok
    | FormTextStoryblok
    | FormFieldsetStoryblok
    | FormMarketingOptInStoryblok
  )[];
};

export interface FormValues {
  [key: string]: unknown;
}

export interface PageBlok {
  _uid?: string;
  name?: string;
  content?: (
    // | AutocompleteStoryblok
    | FormSectionStoryblok
    | FormInputStoryblok
    | FormIbanStoryblok
    | FormCheckboxStoryblok
    | FormTextareaStoryblok
    | FormSelectStoryblok
    | FormFileUploadStoryblok
    | FormDateStoryblok
    | FormTextStoryblok
    | NewLineStoryblok
    | FormFieldsetStoryblok
    | FormMarketingOptInStoryblok
  )[];
  conditions?: FormConditionStoryblok[];
}

export interface FormPageProps {
  pageBlok: PageBlok;
  index: number;
  formValues: { [key: string]: unknown };
  embedded?: boolean;
  form: FormStoryblok;
}

export type TransformedFormData = {
  [key: string]: {
    value: any;
    label: string | undefined;
  };
};
